import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

/**
 * PeriodSelector — the Today / Week / Month toggle shared by both analytics
 * workspaces. It is a pure presentational control: it owns NO data, fires
 * `onChange` with the chosen period key, and reflects the active selection via
 * `aria-pressed` so the choice is accessible (not colour-only).
 *
 * There is intentionally NO "Year" / "Custom" window — the backend summary
 * endpoints take an explicit (start, end) the workspace derives from one of
 * these three named periods. Adding a fourth button would imply a window the
 * backend can't serve.
 */

export type AnalyticsPeriod = "today" | "week" | "month";

const PERIODS: { key: AnalyticsPeriod; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];

export function PeriodSelector({
  value,
  onChange,
}: {
  value: AnalyticsPeriod;
  onChange: (period: AnalyticsPeriod) => void;
}) {
  return (
    <ToggleButtonGroup
      aria-label="Analytics period"
      exclusive
      onChange={(_, next: AnalyticsPeriod | null) => {
        if (next !== null) onChange(next);
      }}
      size="small"
      sx={{
        bgcolor: "action.hover",
        p: 0.5,
        "& .MuiToggleButton-root": {
          border: 0,
          borderRadius: 1.5,
          minHeight: 32,
          px: 1.5,
        },
      }}
      value={value}
    >
      {PERIODS.map(({ key, label }) => (
        <ToggleButton aria-label={label} key={key} value={key}>
          {label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
